// Chek rasmlari (Q-068), brauzer qismi: rasmni oq-qoraga o'girish, ko'rsatish uchun PNG va
// "nomdan keyingi matn"ni belgisi bilan bitta rasmga chizish (termoprinter matn yoniga rasm qo'ya olmaydi).
import { MAX_H, MAX_W, getPx, monoFromGray, trimMono } from './bitmap';
import type { MonoBitmap } from './bitmap';
import type { LineStyle } from './logic';

export function loadImage(src: string | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = typeof src === 'string' ? src : URL.createObjectURL(src);
    if (typeof src === 'string' && /^https?:/.test(src)) img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (typeof src !== 'string') URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => reject(new Error('Rasmni ochib bo‘lmadi — boshqa fayl tanlang (PNG yoki JPG).'));
    img.src = url;
  });
}

/** Rasmni maxW × maxH ichiga sig'dirib oq-qoraga o'giradi (shaffof — oq), oq chetlarsiz */
export function imageToMono(img: HTMLImageElement, maxW: number, maxH: number, darkness: number): MonoBitmap | null {
  const k = Math.min(1, Math.min(maxW, MAX_W) / img.naturalWidth, Math.min(maxH, MAX_H) / img.naturalHeight);
  const w = Math.max(1, Math.round(img.naturalWidth * k));
  const h = Math.max(1, Math.round(img.naturalHeight * k));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data;
  const gray = new Uint8ClampedArray(w * h);
  for (let i = 0; i < w * h; i++) gray[i] = 0.299 * d[i * 4]! + 0.587 * d[i * 4 + 1]! + 0.114 * d[i * 4 + 2]!;
  return trimMono(monoFromGray(gray, w, h, darkness));
}

/** Ko'rsatish uchun: qora nuqtalar, shaffof fon */
export function monoToDataUrl(b: MonoBitmap): string {
  const c = document.createElement('canvas');
  c.width = b.w;
  c.height = b.h;
  const ctx = c.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(b.w, b.h);
  for (let y = 0; y < b.h; y++)
    for (let x = 0; x < b.w; x++)
      if (getPx(b, x, y)) {
        const i = (y * b.w + x) * 4;
        img.data[i + 3] = 255;
      }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL('image/png');
}

// Courier'da belgi eni 0,6 em: 20 px — 12 nuqta, printerning A shrifti bilan bir xil (qatorga shuncha belgi sig'adi)
const FONT_PX = 20;
const GAP = 8;

/**
 * "Nomdan keyingi matn" belgisi bilan: belgi chapda, matn yonida (markazda), yozuv kattaligi va qalinligi
 * chek sozlamasidan. Belgi bo'yi — yozuv bo'yiga mos, iconSize (50–250%) bilan o'zgartiriladi.
 */
export function renderHeaderBlock(icon: MonoBitmap, text: string, style: LineStyle, iconSize: number, width = MAX_W): MonoBitmap | null {
  const sx = style.size === 3 ? 2 : 1;
  const sy = style.size === 1 ? 1 : 2;
  const lineH = Math.round(FONT_PX * 1.25 * sy);
  const iconH = Math.max(8, Math.round(FONT_PX * sy * (iconSize / 100)));
  const iconW = Math.max(1, Math.round((icon.w * iconH) / icon.h));
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const font = `${style.bold ? 'bold ' : ''}${FONT_PX}px "Courier New", Courier, monospace`;
  ctx.font = font;
  const avail = Math.max(40, width - iconW - GAP);
  const lines: string[] = [];
  for (const para of text.split('\n')) {
    let cur = '';
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const next = cur ? `${cur} ${word}` : word;
      if (ctx.measureText(next).width * sx <= avail || !cur) cur = next;
      else {
        lines.push(cur);
        cur = word;
      }
    }
    if (cur) lines.push(cur);
  }
  if (lines.length === 0) return null;
  const textW = Math.min(avail, Math.ceil(Math.max(...lines.map((l) => ctx.measureText(l).width * sx))));
  const textH = lines.length * lineH;
  const h = Math.min(MAX_H, Math.max(iconH, textH) + 4);
  c.width = width;
  c.height = h;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, h);
  const left = Math.max(0, Math.floor((width - (iconW + GAP + textW)) / 2));
  // Belgi — oq-qora nuqtalar (kattalashtirilganda keskin qoladi)
  const iy = Math.floor((h - iconH) / 2);
  ctx.fillStyle = '#000';
  for (let y = 0; y < iconH; y++)
    for (let x = 0; x < iconW; x++)
      if (getPx(icon, Math.floor((x * icon.w) / iconW), Math.floor((y * icon.h) / iconH))) ctx.fillRect(left + x, iy + y, 1, 1);
  // Matn
  ctx.save();
  ctx.font = font;
  ctx.textBaseline = 'middle';
  ctx.translate(left + iconW + GAP, Math.floor((h - textH) / 2));
  ctx.scale(sx, sy);
  lines.forEach((l, i) => ctx.fillText(l, 0, (i * lineH + lineH / 2) / sy));
  ctx.restore();
  const d = ctx.getImageData(0, 0, width, h).data;
  const gray = new Uint8ClampedArray(width * h);
  for (let i = 0; i < width * h; i++) gray[i] = d[i * 4]!;
  return monoFromGray(gray, width, h, 50);
}

